import { Fragment, MouseEvent, ReactElement, useContext, useEffect, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Application, DistributedLoad, LoadCalcType, Loads, LoadType, PointLoad, SpansAndLoads } from 'types/domainTypes';
import { alpha, Divider, FormControl, Menu, MenuItem, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { NumericTextField } from 'components/FormFields/NumericTextField';
import { ShrinkToIconButton } from 'components/ShrinkToIconButton/ShrinkToIconButton';
import AddIcon from '@mui/icons-material/Add';
import { UniformLoadDialog, UniformLoadFormData } from './UniformLoadDialog';
import { DesignSchemeView } from 'components/DesignSchemeView/DesignSchemeView';
import { LoadsPanel } from './LoadsPanel';
import { ConfirmationDialog } from 'components/ConfirmationDialog/ConfirmationDialog';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { isLoadEmpty } from './SpansAndLoadsView';
import { StepModel } from 'components/FormStepper/FormStepper';
import { StepperDialog } from 'components/StepperDialog/StepperDialog';
import { LoadTabs } from './LoadTabs';
import kebabCase from 'lodash/kebabCase';
import camelCase from 'lodash/camelCase';
import useLoads from 'hooks/Loads/LoadsHook';
import { PointLoadDialog, PointLoadFormData } from './PointLoadDialog';
import { v4 as uuid } from 'uuid';
import { DistributedLoadDialog, DistributedLoadFormData } from './DistributedLoadDialog';
import { CalculationContext } from './CalculationContextProvider';

export type SpansAndLoadFormData = {
  span: number | '';
  spans: { value: number | '' }[];
  leftCantilever?: number | '';
  rightCantilever?: number | '';
  permanentLoad?: Loads;
  snowLoad?: Loads;
  pressureLoad?: Loads;
  suctionLoad?: Loads;
  imposedLoad?: Loads;
  constructionLoad?: Loads;
  excepSnowLoad?: Loads;
};

const spansAndLoadFormDefaults: SpansAndLoadFormData = {
  span: '',
  spans: [],
  leftCantilever: '',
  rightCantilever: '',
};

interface SpansAndLoadDialogProps {
  onClose: () => void;
  onSave: (spansAndLoadsData: SpansAndLoads) => Promise<void>;
  spansAndLoadsDetails?: SpansAndLoads | null;
  application: Application;
}

function addIdToLoads(load: Loads | undefined): Loads | undefined {
  if (!load) {
    return;
  }

  return {
    distributedLoads: load.distributedLoads?.map((distributed) => ({
      ...distributed,
      id: uuid(),
    })),
    pointLoads: load.pointLoads?.map((point) => ({ ...point, id: uuid() })),
    uniformLoad: load.uniformLoad,
  };
}

function convertFormData(formData: SpansAndLoadFormData, loadCalculationType: LoadCalcType): SpansAndLoads {
  return {
    loadsCalculationType: loadCalculationType,
    spans: [Number(formData.span), ...formData.spans.map((x) => Number(x.value))],
    leftCantilever: formData.leftCantilever ? Number(formData.leftCantilever) : undefined,
    rightCantilever: formData.rightCantilever ? Number(formData.rightCantilever) : undefined,
    permanentLoads: formData?.permanentLoad,
    snowLoads: formData?.snowLoad,
    pressureLoads: formData?.pressureLoad,
    suctionLoads: formData?.suctionLoad,
    imposedLoads: formData?.imposedLoad,
    constructionLoads: formData?.constructionLoad,
    excepSnowLoads: formData?.excepSnowLoad,
  };
}

function convertSpansAndLoadToFormData(spansAndLoads: SpansAndLoads | null | undefined): SpansAndLoadFormData | undefined {
  if (!spansAndLoads) {
    return;
  }

  return {
    span: spansAndLoads.spans[0] ?? '',
    spans: spansAndLoads.spans.slice(1).map((x) => ({ value: x })),
    leftCantilever: spansAndLoads.leftCantilever ?? '',
    rightCantilever: spansAndLoads.rightCantilever ?? '',
    permanentLoad: addIdToLoads(spansAndLoads.permanentLoads),
    snowLoad: addIdToLoads(spansAndLoads.snowLoads),
    pressureLoad: addIdToLoads(spansAndLoads.pressureLoads),
    suctionLoad: addIdToLoads(spansAndLoads.suctionLoads),
    imposedLoad: addIdToLoads(spansAndLoads.imposedLoads),
    constructionLoad: addIdToLoads(spansAndLoads.constructionLoads),
    excepSnowLoad: addIdToLoads(spansAndLoads.excepSnowLoads),
  };
}

const getTitle = (application: Application, hasSpansAndLoads: boolean) => {
  if (application === Application.RoofPanel) {
    return hasSpansAndLoads
      ? i18n.t('calculation-page.spans-and-loads-dialog.title-roof-edit')
      : i18n.t('calculation-page.spans-and-loads-dialog.title-roof');
  } else {
    return hasSpansAndLoads
      ? i18n.t('calculation-page.spans-and-loads-dialog.title-wall-edit')
      : i18n.t('calculation-page.spans-and-loads-dialog.title-wall');
  }
};

const getAddLoadButtonText = (tabValue: LoadType): string | undefined => {
  switch (tabValue) {
    case LoadType.PermanentLoad:
      return i18n.t('calculation-page.spans-and-loads-dialog.label-add-permanent-load');
    case LoadType.SnowLoad:
      return i18n.t('calculation-page.spans-and-loads-dialog.label-add-snow-load');
    case LoadType.PressureLoad:
      return i18n.t('calculation-page.spans-and-loads-dialog.label-add-pressure-wind-load');
    case LoadType.SuctionLoad:
      return i18n.t('calculation-page.spans-and-loads-dialog.label-add-suction-load');
    case LoadType.ImposedLoad:
      return i18n.t('calculation-page.spans-and-loads-dialog.label-add-imposed-load');
    case LoadType.ConstructionLoad:
      return i18n.t('calculation-page.spans-and-loads-dialog.label-add-construction-load');
    case LoadType.ExcepSnowLoad:
      return i18n.t('calculation-page.spans-and-loads-dialog.label-add-excep-snow-load');
    default:
      return;
  }
};

export function SpansAndLoadDialog({ onClose, onSave, spansAndLoadsDetails, application }: SpansAndLoadDialogProps): ReactElement {
  const { t } = useTranslation();
  const { product } = useContext(CalculationContext);
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm<SpansAndLoadFormData>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: convertSpansAndLoadToFormData(spansAndLoadsDetails) || spansAndLoadFormDefaults,
  });
  const theme = useTheme();
  const { fields, append, remove } = useFieldArray({ control, name: 'spans' });
  const watchAll = useWatch({ control });
  const watchFieldArray = watch('spans');
  const controlledFields = fields.map((field, index) => ({ ...field, ...watchFieldArray[index] }));
  const calcType: LoadCalcType = application === Application.RoofPanel ? LoadCalcType.RoofPanel : LoadCalcType.WallPanel;

  const spansAndLoads = convertFormData(watchAll as SpansAndLoadFormData, calcType);

  enum Steps {
    SpansAndCantilevers,
    AddLoads,
  }

  const [activeSpan, setActiveSpan] = useState(0);
  const [activeStep, setActiveStep] = useState(Steps.SpansAndCantilevers);
  const [isUniformLoadDialogOpen, setIsUniformLoadDialogOpen] = useState(false);
  const [isPointLoadDialogOpen, setIsPointLoadDialogOpen] = useState(false);
  const [isDistributedLoadDialogOpen, setIsDistributedLoadDialogOpen] = useState(false);
  const [screenPosition, setScreenPosition] = useState<null | {
    x: number;
    y: number;
  }>(null);
  const [isDeleteUniformLoadConfirmationOpen, setIsDeleteUniformLoadConfirmationOpen] = useState(false);
  const [isDeletePointLoadConfirmationOpen, setIsDeletePointLoadConfirmationOpen] = useState(false);
  const [isDeleteDistributedLoadConfirmationOpen, setIsDeleteDistributedLoadConfirmationOpen] = useState(false);
  const [isSaveLoadOutOfBoundsError, setIsSaveLoadOutOfBoundsError] = useState(false);
  const open = Boolean(screenPosition);
  const confirmationDialogFullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const [currentPointLoad, setCurrentPointLoad] = useState<PointLoad>();
  const [currentDistributedLoad, setCurrentDistributedLoad] = useState<DistributedLoad>();
  const isWallCalculation = application === Application.WallPanel;
  const maxPointLoads = 7;
  const maxDistributedLoads = 7;
  let spansCantileversLength = (spansAndLoads.leftCantilever || 0) + (spansAndLoads.rightCantilever || 0);
  for (let i = 0; i < spansAndLoads.spans.length; i++) {
    const span = spansAndLoads.spans[i];
    spansCantileversLength += span;
  }

  const getDefaultLoadTabValue = () => {
    if (!isLoadEmpty(watch('permanentLoad'))) {
      return LoadType.PermanentLoad;
    } else if (!isLoadEmpty(watch('snowLoad'))) {
      return LoadType.SnowLoad;
    } else if (!isLoadEmpty(watch('pressureLoad'))) {
      return LoadType.PressureLoad;
    } else if (!isLoadEmpty(watch('suctionLoad'))) {
      return LoadType.SuctionLoad;
    } else if (!isLoadEmpty(watch('imposedLoad'))) {
      return LoadType.ImposedLoad;
    } else if (!isLoadEmpty(watch('constructionLoad'))) {
      return LoadType.ConstructionLoad;
    } else if (!isLoadEmpty(watch('excepSnowLoad'))) {
      return LoadType.ExcepSnowLoad;
    }
    return isWallCalculation ? LoadType.PressureLoad : LoadType.PermanentLoad;
  };

  const [tabValue, setTabValue] = useState(getDefaultLoadTabValue());
  const { loads, label } = useLoads(tabValue, spansAndLoads);

  const onTabChange = (newValue: LoadType) => {
    setTabValue(newValue);
  };

  const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
    setScreenPosition({ x: event.clientX, y: event.clientY });
  };
  const handleMenuClose = () => {
    setScreenPosition(null);
  };

  const handleMenuItemClick = (func: () => void) => {
    handleMenuClose();
    func();
  };

  useEffect(() => {
    window.addEventListener('resize', handleMenuClose);

    return () => {
      window.removeEventListener('resize', handleMenuClose);
    };
  });

  const onSaveHandler = async (formData: SpansAndLoadFormData) => {
    let saveData = convertFormData(formData, calcType);

    if (calcType === LoadCalcType.RoofPanel) {
      saveData = {
        ...saveData,
        permanentLoads: {
          ...saveData.permanentLoads,
          selfWeight: product?.selfWeight,
        },
      };
    }
    const isPointLoadInBounds = (point: PointLoad) => point.position <= spansCantileversLength;
    const isDistributedLoadInBounds = (distributed: DistributedLoad) =>
      distributed.positionStart <= spansCantileversLength && distributed.positionEnd <= spansCantileversLength;

    if (
      saveData.pressureLoads?.pointLoads?.every(isPointLoadInBounds) !== false &&
      saveData.suctionLoads?.pointLoads?.every(isPointLoadInBounds) !== false &&
      saveData.imposedLoads?.pointLoads?.every(isPointLoadInBounds) !== false &&
      saveData.snowLoads?.pointLoads?.every(isPointLoadInBounds) !== false &&
      saveData.excepSnowLoads?.pointLoads?.every(isPointLoadInBounds) !== false &&
      saveData.constructionLoads?.pointLoads?.every(isPointLoadInBounds) !== false &&
      saveData.permanentLoads?.pointLoads?.every(isPointLoadInBounds) !== false &&
      saveData.pressureLoads?.distributedLoads?.every(isDistributedLoadInBounds) !== false &&
      saveData.suctionLoads?.distributedLoads?.every(isDistributedLoadInBounds) !== false &&
      saveData.imposedLoads?.distributedLoads?.every(isDistributedLoadInBounds) !== false &&
      saveData.snowLoads?.distributedLoads?.every(isDistributedLoadInBounds) !== false &&
      saveData.excepSnowLoads?.distributedLoads?.every(isDistributedLoadInBounds) !== false &&
      saveData.constructionLoads?.distributedLoads?.every(isDistributedLoadInBounds) !== false &&
      saveData.permanentLoads?.distributedLoads?.every(isDistributedLoadInBounds) !== false
    ) {
      await onSave(saveData);
    } else {
      setIsSaveLoadOutOfBoundsError(true);
    }
  };

  const onAddAnotherSpan = () => {
    setActiveSpan(fields.length);
    append({ value: '' });
  };

  const onRemoveSpan = (index: number) => {
    remove(index);
  };

  // Static type needed for TypeScript linting - use of setValue() and watch()
  type LoadString = 'permanentLoad' | 'suctionLoad' | 'pressureLoad' | 'snowLoad' | 'imposedLoad' | 'constructionLoad' | 'excepSnowLoad';

  const setUniformLoad = (loadValue?: number) => {
    const valueToSet = camelCase(tabValue) as LoadString;

    setValue(`${valueToSet}.uniformLoad`, loadValue);
  };

  const setDistributedLoad = (distributedLoad: DistributedLoad) => {
    const valueToSet = camelCase(tabValue) as LoadString;
    let distributedLoads = watch(`${valueToSet}.distributedLoads`)?.slice();

    if (!distributedLoads) {
      distributedLoads = [];
    }

    if (!distributedLoad.id) {
      distributedLoad.id = uuid();
    }

    const existingIndex = distributedLoads?.findIndex((element) => element.id === distributedLoad.id);

    if (existingIndex !== -1) {
      distributedLoads[existingIndex] = distributedLoad;
    } else {
      distributedLoads?.push(distributedLoad);
    }

    setValue(`${valueToSet}.distributedLoads`, distributedLoads);
  };

  const setPointLoad = (pointLoad: PointLoad) => {
    const valueToSet = camelCase(tabValue) as LoadString;
    let pointLoads = watch(`${valueToSet}.pointLoads`)?.slice();

    if (!pointLoads) {
      pointLoads = [];
    }

    if (!pointLoad.id) {
      pointLoad.id = uuid();
    }

    const existingIndex = pointLoads?.findIndex((element) => element.id === pointLoad.id);

    if (existingIndex !== -1) {
      pointLoads[existingIndex] = pointLoad;
    } else {
      pointLoads?.push(pointLoad);
    }

    setValue(`${valueToSet}.pointLoads`, pointLoads);
  };

  const deletePointLoad = (pointLoad: PointLoad) => {
    const valueToSet = camelCase(tabValue) as LoadString;
    let pointLoads = watch(`${valueToSet}.pointLoads`)?.slice();

    if (!pointLoads) {
      pointLoads = [];
    }

    const existingIndex = pointLoads?.findIndex((element) => element.id === pointLoad.id);

    if (existingIndex !== -1) {
      pointLoads.splice(existingIndex, 1);
    }
    setValue(`${valueToSet}.pointLoads`, pointLoads);
  };

  const deleteDistributedLoad = (distributedLoad: DistributedLoad) => {
    const valueToSet = camelCase(tabValue) as LoadString;
    let distributedLoads = watch(`${valueToSet}.distributedLoads`)?.slice();

    if (!distributedLoads) {
      distributedLoads = [];
    }

    const existingIndex = distributedLoads?.findIndex((element) => element.id === distributedLoad.id);

    if (existingIndex !== -1) {
      distributedLoads.splice(existingIndex, 1);
    }
    setValue(`${valueToSet}.distributedLoads`, distributedLoads);
  };

  const onUniformLoadSave = (formData: UniformLoadFormData) => {
    setUniformLoad(formData.uniformLoad as number);
    setIsUniformLoadDialogOpen(false);
  };

  const onPointLoadSave = (formData: PointLoadFormData) => {
    setPointLoad({
      load: formData.pointLoad as number,
      position: formData.location as number,
      id: formData.id,
    });
    setCurrentPointLoad(undefined);
    setIsPointLoadDialogOpen(false);
  };

  const onDistributedLoadSave = (formData: DistributedLoadFormData) => {
    setDistributedLoad({
      loadStart: formData.startLoad as number,
      loadEnd: formData.endLoad as number,
      positionStart: formData.startLocation as number,
      positionEnd: formData.endLocation as number,
      id: formData.id,
    });
    setCurrentDistributedLoad(undefined);
    setIsDistributedLoadDialogOpen(false);
  };

  const onDeleteUniform = () => {
    setIsDeleteUniformLoadConfirmationOpen(true);
  };

  const onEditPointLoad = (pointLoad: PointLoad) => {
    setCurrentPointLoad(pointLoad);
    setIsPointLoadDialogOpen(true);
  };

  const onDeletePointLoad = (pointLoad: PointLoad) => {
    setCurrentPointLoad(pointLoad);
    setIsDeletePointLoadConfirmationOpen(true);
  };

  const onEditDistributedLoad = (distributedLoad: DistributedLoad) => {
    setCurrentDistributedLoad(distributedLoad);
    setIsDistributedLoadDialogOpen(true);
  };

  const onDeleteDistributedLoad = (distributedLoad: DistributedLoad) => {
    setCurrentDistributedLoad(distributedLoad);
    setIsDeleteDistributedLoadConfirmationOpen(true);
  };

  const onDeleteUniformLoad = () => {
    setUniformLoad(undefined);
    setIsDeleteUniformLoadConfirmationOpen(false);
  };

  const addLoadsStep: StepModel = {
    text: t('calculation-page.spans-and-loads-dialog.tab-add-loads'),
    value: Steps.AddLoads,
    caption: t('calculation-page.spans-and-loads-dialog.tab-add-loads-optional'),
  };

  const spansAndCantileversStep: StepModel = {
    text: t('calculation-page.spans-and-loads-dialog.tab-add-spans-and-catilevers'),
    value: Steps.SpansAndCantilevers,
  };

  const stepperOptions: StepModel[] = [addLoadsStep, spansAndCantileversStep];

  return (
    <>
      <StepperDialog<SpansAndLoadFormData>
        title={getTitle(application, !!spansAndLoadsDetails)}
        onClose={onClose}
        saveActionText={t('calculation-page.spans-and-loads-dialog.finish-button')}
        isDirty={isDirty}
        steps={stepperOptions}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        handleSubmit={handleSubmit}
        onSave={onSaveHandler}
        hasOptionalSteps
        stepperWidth={'75%'}
      >
        <>
          {activeStep === Steps.AddLoads && (
            <>
              <Divider style={{ marginBottom: 0 }} />
              <LoadTabs tabValue={tabValue} onTabChange={onTabChange} isWallCalculation={isWallCalculation} />
            </>
          )}

          <FormControl sx={{ overflowY: 'auto' }} data-testid="span-and-load-form">
            <DesignSchemeView
              style={{
                margin: '0 0 15px',
                maxHeight: '316px',
                width: 'auto',
              }}
              spansAndLoads={convertFormData(watchAll as SpansAndLoadFormData, calcType)}
              loadType={activeStep === 0 ? LoadType.NotRequired : tabValue}
            />
            {activeStep === Steps.SpansAndCantilevers && (
              // Panel 1 - Add Spans and cantilevers
              <>
                <Stack sx={{ padding: 2 }} spacing={2}>
                  <NumericTextField
                    name="span"
                    control={control}
                    label={t('calculation-page.spans-and-loads-dialog.label-span', { index: 1 })}
                    fullWidth
                    required
                    adornmentProps={{ endAdornment: 'm' }}
                    autoFocus={activeSpan === 0}
                    decimalPlaces={4}
                    validateOnBlur
                    data-testid="span1"
                  />
                  {controlledFields.map((field, index) => (
                    <Fragment key={field.id}>
                      <Stack alignItems="flex-end">
                        <NumericTextField
                          name={`spans.${index}.value`}
                          control={control}
                          label={t('calculation-page.spans-and-loads-dialog.label-span', { index: index + 2 })}
                          fullWidth
                          required
                          adornmentProps={{ endAdornment: 'm' }}
                          autoFocus={activeSpan === index}
                          decimalPlaces={4}
                          validateOnBlur
                        />
                        <div style={{ marginTop: '-26px' }}>
                          <ShrinkToIconButton
                            hideTextWhenXS={false}
                            icon={<></>}
                            onClick={() => onRemoveSpan(index)}
                            buttonText={t('calculation-page.spans-and-loads-dialog.label-remove-span')}
                            transformButtonText={false}
                          />
                        </div>
                      </Stack>
                    </Fragment>
                  ))}
                  {controlledFields.length < 10 && (
                    <ShrinkToIconButton
                      hideTextWhenXS={false}
                      onClick={onAddAnotherSpan}
                      icon={<AddIcon />}
                      buttonText={t('calculation-page.spans-and-loads-dialog.label-add-span')}
                      transformButtonText={false}
                    />
                  )}
                  <NumericTextField
                    name="leftCantilever"
                    control={control}
                    label={t('calculation-page.spans-and-loads-dialog.label-left-cantilever')}
                    fullWidth
                    adornmentProps={{ endAdornment: 'm' }}
                    decimalPlaces={4}
                    validateOnBlur
                  />
                  <NumericTextField
                    name="rightCantilever"
                    control={control}
                    label={t('calculation-page.spans-and-loads-dialog.label-right-cantilever')}
                    fullWidth
                    adornmentProps={{ endAdornment: 'm' }}
                    decimalPlaces={4}
                    validateOnBlur
                  />
                </Stack>
              </>
            )}
            {activeStep === Steps.AddLoads && (
              // Panel 2 - Add loads
              <>
                <LoadsPanel
                  loadLabel={label}
                  loadType={tabValue}
                  loads={loads}
                  onEditUniform={() => setIsUniformLoadDialogOpen(true)}
                  onDeleteUniform={onDeleteUniform}
                  onEditPointLoad={onEditPointLoad}
                  onDeletePointLoad={onDeletePointLoad}
                  onEditDistributedLoad={onEditDistributedLoad}
                  onDeleteDistributedLoad={onDeleteDistributedLoad}
                />
                <Stack sx={{ padding: 2 }} spacing={2}>
                  <Stack justifyContent="center" alignItems="center" spacing={2}>
                    <ShrinkToIconButton variant="contained" onClick={handleMenuClick} buttonText={getAddLoadButtonText(tabValue)} />
                    <Typography width="50%" textAlign="center" variant="body2" color={alpha(theme.palette.common.black, 0.6)}>
                      {t('calculation-page.spans-and-loads-dialog.label-add-load-caption')}
                    </Typography>
                  </Stack>
                </Stack>
                <Menu
                  id="menu"
                  open={open}
                  anchorReference="anchorPosition"
                  anchorPosition={
                    screenPosition
                      ? {
                          top: screenPosition.y,
                          left: screenPosition.x,
                        }
                      : { top: 0, left: 0 }
                  }
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  onClose={handleMenuClose}
                >
                  <MenuItem onClick={() => handleMenuItemClick(() => setIsUniformLoadDialogOpen(true))} disabled={!!loads?.uniformLoad}>
                    {t('calculation-page.spans-and-loads-dialog.label-uniform-load')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleMenuItemClick(() => setIsPointLoadDialogOpen(true))}
                    disabled={loads?.pointLoads && loads?.pointLoads?.length >= maxPointLoads}
                  >
                    {t('calculation-page.spans-and-loads-dialog.label-point-load')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleMenuItemClick(() => setIsDistributedLoadDialogOpen(true))}
                    disabled={loads?.distributedLoads && loads?.distributedLoads?.length >= maxDistributedLoads}
                  >
                    {t('calculation-page.spans-and-loads-dialog.label-distributed-load')}
                  </MenuItem>
                </Menu>
                {isUniformLoadDialogOpen && (
                  <UniformLoadDialog
                    uniformLoad={loads?.uniformLoad}
                    title={
                      loads?.uniformLoad
                        ? t(`calculation-page.spans-and-loads-dialog.uniform-load-dialog.${kebabCase(tabValue)}-title-edit`)
                        : t(`calculation-page.spans-and-loads-dialog.uniform-load-dialog.${kebabCase(tabValue)}-title`)
                    }
                    loadType={tabValue}
                    onClose={() => setIsUniformLoadDialogOpen(false)}
                    onSave={onUniformLoadSave}
                  />
                )}
                {isPointLoadDialogOpen && (
                  <PointLoadDialog
                    formData={currentPointLoad}
                    title={
                      currentPointLoad
                        ? t(`calculation-page.spans-and-loads-dialog.point-load-dialog.${kebabCase(tabValue)}-title-edit`)
                        : t(`calculation-page.spans-and-loads-dialog.point-load-dialog.${kebabCase(tabValue)}-title`)
                    }
                    loadType={tabValue}
                    onClose={() => {
                      setCurrentPointLoad(undefined);
                      setIsPointLoadDialogOpen(false);
                    }}
                    onSave={onPointLoadSave}
                    maxLocationValue={spansCantileversLength}
                  />
                )}
                {isDistributedLoadDialogOpen && (
                  <DistributedLoadDialog
                    formData={currentDistributedLoad}
                    title={
                      currentDistributedLoad
                        ? t(`calculation-page.spans-and-loads-dialog.distributed-load-dialog.${kebabCase(tabValue)}-title-edit`)
                        : t(`calculation-page.spans-and-loads-dialog.distributed-load-dialog.${kebabCase(tabValue)}-title`)
                    }
                    loadType={tabValue}
                    onClose={() => {
                      setCurrentDistributedLoad(undefined);
                      setIsDistributedLoadDialogOpen(false);
                    }}
                    onSave={onDistributedLoadSave}
                    maxLocation={spansCantileversLength}
                  />
                )}
              </>
            )}
          </FormControl>
        </>
      </StepperDialog>
      <ConfirmationDialog
        isOpen={isDeleteUniformLoadConfirmationOpen}
        handleClose={() => setIsDeleteUniformLoadConfirmationOpen(false)}
        title={t('calculation-page.spans-and-loads-dialog.delete-confirmation.uniform-load.title')}
        questionText={t('calculation-page.spans-and-loads-dialog.delete-confirmation.uniform-load.question-text')}
        handleConfirm={() => {
          onDeleteUniformLoad();
        }}
        isFullscreen={confirmationDialogFullScreen}
        cancelText={t('calculation-page.spans-and-loads-dialog.delete-confirmation.cancel-text')}
        confirmText={t('calculation-page.spans-and-loads-dialog.delete-confirmation.confirm-text')}
        titleIcon={<WarningAmberIcon />}
      ></ConfirmationDialog>
      <ConfirmationDialog
        isOpen={isDeletePointLoadConfirmationOpen}
        handleClose={() => {
          setIsDeletePointLoadConfirmationOpen(false);
          setCurrentPointLoad(undefined);
        }}
        title={t('calculation-page.spans-and-loads-dialog.delete-confirmation.point-load.title')}
        questionText={t('calculation-page.spans-and-loads-dialog.delete-confirmation.point-load.question-text')}
        handleConfirm={() => {
          if (currentPointLoad) {
            deletePointLoad(currentPointLoad);
            setCurrentPointLoad(undefined);
            setIsDeletePointLoadConfirmationOpen(false);
          }
        }}
        isFullscreen={confirmationDialogFullScreen}
        cancelText={t('calculation-page.spans-and-loads-dialog.delete-confirmation.cancel-text')}
        confirmText={t('calculation-page.spans-and-loads-dialog.delete-confirmation.confirm-text')}
        titleIcon={<WarningAmberIcon />}
      ></ConfirmationDialog>
      <ConfirmationDialog
        isOpen={isDeleteDistributedLoadConfirmationOpen}
        handleClose={() => {
          setIsDeleteDistributedLoadConfirmationOpen(false);
          setCurrentDistributedLoad(undefined);
        }}
        title={t('calculation-page.spans-and-loads-dialog.delete-confirmation.distributed-load.title')}
        questionText={t('calculation-page.spans-and-loads-dialog.delete-confirmation.distributed-load.question-text')}
        handleConfirm={() => {
          if (currentDistributedLoad) {
            deleteDistributedLoad(currentDistributedLoad);
            setCurrentDistributedLoad(undefined);
            setIsDeleteDistributedLoadConfirmationOpen(false);
          }
        }}
        isFullscreen={confirmationDialogFullScreen}
        cancelText={t('calculation-page.spans-and-loads-dialog.delete-confirmation.cancel-text')}
        confirmText={t('calculation-page.spans-and-loads-dialog.delete-confirmation.confirm-text')}
        titleIcon={<WarningAmberIcon />}
      ></ConfirmationDialog>
      <ConfirmationDialog
        isOpen={isSaveLoadOutOfBoundsError}
        handleClose={() => setIsSaveLoadOutOfBoundsError(false)}
        title={t('calculation-page.spans-and-loads-dialog.save-error-dialog.title')}
        questionText={t('calculation-page.spans-and-loads-dialog.save-error-dialog.question-text')}
        handleConfirm={() => setIsSaveLoadOutOfBoundsError(false)}
        confirmText={t('calculation-page.spans-and-loads-dialog.save-error-dialog.confirm-text')}
        isFullscreen={confirmationDialogFullScreen}
        titleIcon={<WarningAmberIcon />}
      />
    </>
  );
}
